import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AdminShell } from "./components/AdminShell";
import { LoadingState } from "./components/States";
import { RequireAuth, RequireRole } from "./components/RouteGuards";
import { RequireAdminAuth } from "./components/RequireAdminAuth";
import { BrowsePage } from "./pages/BrowsePage";
import { ListingDetailPage } from "./pages/ListingDetailPage";
import { SellerProfilePage } from "./pages/SellerProfilePage";
import { SignInPage } from "./pages/SignInPage";
import { WalletPage } from "./pages/WalletPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AboutPage } from "./pages/AboutPage";
import { HowItWorksPage } from "./pages/HowItWorksPage";
import { FaqPage } from "./pages/FaqPage";
import { ContactPage } from "./pages/ContactPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { SellerDashboardPage } from "./pages/SellerDashboardPage";
import { SellerAnalyticsPage } from "./pages/SellerAnalyticsPage";
import { CreateEditListingPage } from "./pages/CreateEditListingPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { BuyerOrdersPage } from "./pages/BuyerOrdersPage";
import { SellerOrdersPage } from "./pages/SellerOrdersPage";
import { OrderDetailPage } from "./pages/OrderDetailPage";
import { MessagesPage } from "./pages/MessagesPage";
import { TicketsPage } from "./pages/TicketsPage";
import { WatchlistPage } from "./pages/WatchlistPage";
import { MyOffersPage } from "./pages/MyOffersPage";
import { SellerOffersPage } from "./pages/SellerOffersPage";
import { NewTicketPage } from "./pages/NewTicketPage";
import { TicketDetailPage } from "./pages/TicketDetailPage";
import { AdminLoginPage } from "./pages/admin/AdminLoginPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminUserDetailPage } from "./pages/admin/AdminUserDetailPage";
import { AdminListingsPage } from "./pages/admin/AdminListingsPage";
import { AdminDisputesPage } from "./pages/admin/AdminDisputesPage";
import { AdminTicketsPage } from "./pages/admin/AdminTicketsPage";
import { AdminTicketDetailPage } from "./pages/admin/AdminTicketDetailPage";
import { AdminCouponsPage } from "./pages/admin/AdminCouponsPage";
import { AdminPostsPage } from "./pages/admin/AdminPostsPage";
import { AdminPostEditPage } from "./pages/admin/AdminPostEditPage";
import { BlogListPage } from "./pages/BlogListPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { AdminAccountTransfersPage } from "./pages/admin/AdminAccountTransfersPage";
import { AdminDomainTransfersPage } from "./pages/admin/AdminDomainTransfersPage";
import { AdminFlaggedMessagesPage } from "./pages/admin/AdminFlaggedMessagesPage";
import { AdminAuditLogPage } from "./pages/admin/AdminAuditLogPage";
import { AdminMaintenancePage } from "./pages/admin/AdminMaintenancePage";
import { DisputeSubmitPage } from "./pages/DisputeSubmitPage";
import { DisputeDetailPage } from "./pages/DisputeDetailPage";
import { ModerationPage } from "./pages/ModerationPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ErrorPage } from "./pages/ErrorPage";

// Redoc (the API docs renderer) is a large dependency that almost no visitor needs — code-split
// it into its own chunk so it only loads for someone who actually opens /api-docs, instead of
// bloating the bundle everyone downloads to browse listings.
const ApiDocsPage = lazy(() =>
  import("./pages/ApiDocsPage").then((m) => ({ default: m.ApiDocsPage })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppShell />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <BrowsePage /> },
      { path: "listings/:id", element: <ListingDetailPage /> },
      { path: "sellers/:id", element: <SellerProfilePage /> },
      { path: "sign-in", element: <SignInPage /> },

      // Public content pages. These are the site's main indexable surface besides listings —
      // see robots.txt and the sitemap in apps/web/server.mjs, which must be kept in sync.
      { path: "blog", element: <BlogListPage /> },
      { path: "blog/:slug", element: <BlogPostPage /> },
      { path: "about", element: <AboutPage /> },
      { path: "how-it-works", element: <HowItWorksPage /> },
      { path: "faq", element: <FaqPage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "privacy", element: <PrivacyPage /> },
      { path: "terms", element: <TermsPage /> },
      {
        path: "api-docs",
        element: (
          <Suspense fallback={<LoadingState label="Loading API documentation…" />}>
            <ApiDocsPage />
          </Suspense>
        ),
      },

      {
        path: "profile",
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      {
        path: "wallet",
        element: (
          <RequireAuth>
            <WalletPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell",
        element: (
          <RequireAuth>
            <SellerDashboardPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell/orders",
        element: (
          <RequireAuth>
            <SellerOrdersPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell/analytics",
        element: (
          <RequireAuth>
            <SellerAnalyticsPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell/new",
        element: (
          <RequireAuth>
            <CreateEditListingPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell/:id/edit",
        element: (
          <RequireAuth>
            <CreateEditListingPage />
          </RequireAuth>
        ),
      },
      {
        path: "checkout/:listingId",
        element: (
          <RequireAuth>
            <CheckoutPage />
          </RequireAuth>
        ),
      },
      {
        path: "orders",
        element: (
          <RequireAuth>
            <BuyerOrdersPage />
          </RequireAuth>
        ),
      },
      {
        path: "messages",
        element: (
          <RequireAuth>
            <MessagesPage />
          </RequireAuth>
        ),
      },
      {
        path: "messages/:conversationId",
        element: (
          <RequireAuth>
            <MessagesPage />
          </RequireAuth>
        ),
      },
      {
        path: "watchlist",
        element: (
          <RequireAuth>
            <WatchlistPage />
          </RequireAuth>
        ),
      },
      {
        path: "offers",
        element: (
          <RequireAuth>
            <MyOffersPage />
          </RequireAuth>
        ),
      },
      {
        path: "sell/offers",
        element: (
          <RequireAuth>
            <SellerOffersPage />
          </RequireAuth>
        ),
      },
      {
        path: "tickets",
        element: (
          <RequireAuth>
            <TicketsPage />
          </RequireAuth>
        ),
      },
      {
        path: "tickets/new",
        element: (
          <RequireAuth>
            <NewTicketPage />
          </RequireAuth>
        ),
      },
      {
        path: "tickets/:id",
        element: (
          <RequireAuth>
            <TicketDetailPage />
          </RequireAuth>
        ),
      },
      {
        path: "orders/:orderId",
        element: (
          <RequireAuth>
            <OrderDetailPage />
          </RequireAuth>
        ),
      },
      {
        path: "orders/:orderId/dispute",
        element: (
          <RequireAuth>
            <DisputeSubmitPage />
          </RequireAuth>
        ),
      },
      {
        path: "disputes/:disputeId",
        element: (
          <RequireAuth>
            <DisputeDetailPage />
          </RequireAuth>
        ),
      },
      {
        path: "moderation",
        element: (
          <RequireRole roles={["MODERATOR", "ADMIN"]}>
            <ModerationPage />
          </RequireRole>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },

  // Platform-operator admin dashboard — a wholly separate route tree (own auth, own layout,
  // no public-site header/footer/wallet). /admin/login is unguarded (that's the login form
  // itself); every other /admin/* route requires a valid admin session.
  { path: "/admin/login", element: <AdminLoginPage /> },
  {
    path: "/admin",
    element: (
      <RequireAdminAuth>
        <AdminShell />
      </RequireAdminAuth>
    ),
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: "users", element: <AdminUsersPage /> },
      { path: "users/:id", element: <AdminUserDetailPage /> },
      { path: "listings", element: <AdminListingsPage /> },
      { path: "disputes", element: <AdminDisputesPage /> },
      { path: "tickets", element: <AdminTicketsPage /> },
      { path: "tickets/:id", element: <AdminTicketDetailPage /> },
      { path: "coupons", element: <AdminCouponsPage /> },
      { path: "account-transfers", element: <AdminAccountTransfersPage /> },
      { path: "domain-transfers", element: <AdminDomainTransfersPage /> },
      { path: "posts", element: <AdminPostsPage /> },
      { path: "posts/new", element: <AdminPostEditPage /> },
      { path: "posts/:id", element: <AdminPostEditPage /> },
      { path: "flagged-messages", element: <AdminFlaggedMessagesPage /> },
      { path: "audit-log", element: <AdminAuditLogPage /> },
      { path: "maintenance", element: <AdminMaintenancePage /> },
    ],
  },
]);
